/**
 * Helpers for pointing a seller at the fields that block a form.
 *
 * Works off the DOM rather than a parallel table of field names → labels:
 * the label a person sees IS the label the summary names, so the two can't
 * drift. A field is found by its `name` attribute (what final-form reports
 * errors under), or by a `data-field-anchor` for things that aren't inputs
 * (the photo dropzone, a radio group).
 */

import { ARRAY_ERROR } from 'final-form';

/** Flatten final-form's nested `errors` into dotted field names. */
export const flattenErrorKeys = (errors, prefix = '') => {
  if (!errors || typeof errors !== 'object') {
    return [];
  }
  return Object.entries(errors).flatMap(([key, value]) => {
    // A FieldArray's own error hangs off the array under ARRAY_ERROR (a
    // string key in this final-form, so `Object.keys` sees it); the array
    // itself is what's wrong, so report the array's own name.
    if (Array.isArray(value)) {
      const own = value[ARRAY_ERROR] != null || value.some(v => v != null);
      return own ? [prefix + key] : [];
    }
    if (value && typeof value === 'object') {
      return flattenErrorKeys(value, `${prefix}${key}.`);
    }
    return value ? [prefix + key] : [];
  });
};

const escapeSelector = value =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/"/g, '\\"');

/** The element to scroll to and focus for a field name, within `root`. */
export const findFieldElement = (root, name) => {
  if (!root) {
    return null;
  }
  const n = escapeSelector(name);
  return (
    root.querySelector(`[data-field-anchor="${n}"]`) ||
    root.querySelector(`[name="${n}"]:not([type="hidden"])`) ||
    null
  );
};

/** Text of the label a person sees for that element, marker stripped. */
export const findFieldLabel = (root, name) => {
  const el = findFieldElement(root, name);
  if (!el) {
    return null;
  }
  const explicit = el.getAttribute('data-field-label');
  if (explicit) {
    return explicit;
  }
  let text = '';
  if (el.id) {
    const label = root.querySelector(`label[for="${escapeSelector(el.id)}"]`);
    if (label) {
      text = label.textContent;
    }
  }
  if (!text && el.labels && el.labels.length > 0) {
    // Implicit association: the input sits inside its <label>.
    text = el.labels[0].textContent;
  }
  if (!text) {
    const labelled = el.getAttribute('aria-labelledby');
    const byId = labelled && root.ownerDocument.getElementById(labelled);
    text = byId ? byId.textContent : el.getAttribute('aria-label') || '';
  }
  if (!text) {
    const fieldset = el.closest('fieldset');
    const legend = fieldset && fieldset.querySelector('legend');
    text = legend ? legend.textContent : '';
  }
  // Drop the required/optional markers the label carries.
  return (
    text
      .replace(/\*/g, '')
      .replace(/\((valgfritt|optional)\)/i, '')
      .replace(/\s+/g, ' ')
      .trim() || null
  );
};

const FLASH_ATTR = 'data-field-flash';
const FLASH_MS = 1200;
// Marks what the page is scrolled to; the CSS turns --fieldScrollGap into
// its scroll margin. The value is measured here: the field's own gap above
// plus whatever sticky header turns out to be covering the top of the
// viewport on that page (pages differ, so it is not assumed).
const SCROLL_TARGET_ATTR = 'data-field-scroll-target';
const SCROLL_GAP_PROP = '--fieldScrollGap';

/** The block a person sees as "the field": its wrapper (label + input). */
const fieldBlock = el => {
  const label = el.labels && el.labels[0];
  const wrapper = label && label.parentElement;
  if (wrapper && wrapper.tagName !== 'FORM') {
    return wrapper;
  }
  return label || el;
};

/**
 * Height of a sticky or fixed header sitting over the top of the viewport
 * above `block`, 0 if the top edge is clear. Measured after the block has
 * been scrolled to the top, by asking what's painted at that spot.
 */
const coveringHeaderHeight = block => {
  if (typeof document.elementFromPoint !== 'function') {
    return 0;
  }
  const rect = block.getBoundingClientRect();
  let node = document.elementFromPoint(rect.left + rect.width / 2, 1);
  while (node && node !== block && !block.contains(node)) {
    const position = window.getComputedStyle(node).position;
    if (position === 'fixed' || position === 'sticky') {
      return Math.max(0, node.getBoundingClientRect().bottom);
    }
    node = node.parentElement;
  }
  return 0;
};

/**
 * Vertical space above the block: its own top margin plus the row gap of
 * the nearest ancestor that lays its children out with a gap. Scrolling
 * with this as margin lands the block at the top with exactly its usual
 * breathing room above it.
 */
const spaceAbove = (block, formEl) => {
  const px = value => parseFloat(value) || 0;
  let space = px(window.getComputedStyle(block).marginTop);
  let node = block;
  while (node && node !== formEl && node.parentElement) {
    const rowGap = px(window.getComputedStyle(node.parentElement).rowGap);
    if (rowGap > 0) {
      space += rowGap;
      break;
    }
    node = node.parentElement;
  }
  return space;
};

/**
 * Bring a field into view and give it focus. The scroll is an instant jump
 * on purpose: a smooth scroll is an animation the browser may drop when
 * focus moves or the form re-renders under it, and a jump that always
 * lands beats a glide that sometimes doesn't. The field also gets a short
 * fading ring (see FormErrorSummary.module.css): on a desktop the whole
 * form may already be on screen and an invalid field's border is red
 * before and after focus, so without it the click looks like it did
 * nothing.
 */
export const scrollToAndFocus = el => {
  if (!el) {
    return;
  }
  // Land with the top of the field block at the top of the viewport (below
  // the topbar, keeping the block's own gap above it), so the seller reads
  // it top-down: label, input, message.
  const target = fieldBlock(el);
  if (typeof target.scrollIntoView === 'function' && typeof window !== 'undefined') {
    const gap = spaceAbove(target, el.closest('form'));
    const scrollWithMargin = px => {
      target.style.setProperty(SCROLL_GAP_PROP, `${px}px`);
      target.setAttribute(SCROLL_TARGET_ATTR, '');
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      target.removeAttribute(SCROLL_TARGET_ATTR);
      target.style.removeProperty(SCROLL_GAP_PROP);
    };
    scrollWithMargin(gap);
    // If a sticky header now sits over the block, push it down by that
    // much. Both scrolls happen before the next paint, so only the final
    // position is ever seen.
    const header = coveringHeaderHeight(target);
    if (header > 0) {
      scrollWithMargin(gap + header);
    }
  }
  if (typeof el.focus === 'function') {
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }
  // Re-trigger the ring even when the same field is clicked twice.
  el.removeAttribute(FLASH_ATTR);
  // eslint-disable-next-line no-unused-expressions
  el.offsetWidth;
  el.setAttribute(FLASH_ATTR, '');
  window.setTimeout(() => {
    el.removeAttribute(FLASH_ATTR);
  }, FLASH_MS);
};
